export const CALENDLY = 'https://calendly.com/yovel-yovaiagency/new-meeting';
export const CONTACT_EMAIL = 'yovel@yovaiagency.com';
export const CONTACT_PHONE = '(470) 298-0440';
export const CONTACT_PHONE_HREF = 'tel:+14702980440';
export const MOTTO = 'Business Communication & Automation Systems';

export const openCal = () => window.open(CALENDLY, '_blank', 'noopener,noreferrer');
export const scrollToId = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  el.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'start' });
};

export const NAV_LINKS = [
  { id: 'solutions', label: 'Solutions' },
  { id: 'industries', label: 'Industries' },
  { id: 'journey', label: 'How It Works' },
  { id: 'systems', label: 'Packages' },
  { id: 'process', label: 'Process' },
  { id: 'faq', label: 'FAQ' },
];

export const applyNow = (solution) => {
  if (solution) window.dispatchEvent(new CustomEvent('yv:package', { detail: solution }));
  scrollToId('contact');
};

export const PLANS = [
  {
    id: 'lead-recovery', n: '01', name: 'Lead Recovery System',
    positioning: 'Lead Response, Recovery & Follow-Up Management',
    implement: [
      ['Lead Recovery Audit & Response Standards', 'Review recent calls and web inquiries at kickoff to establish baseline response times and define follow-up ownership.'],
      ['Missed-Call Recovery'], ['Automated New-Inquiry Follow-Up'], ['CRM Integration & Lead Routing'], ['Appointment Requests & Confirmations'],
      ['Lead Ownership & Overdue Follow-Up Escalation', 'Every inquiry has an owner, and overdue follow-ups are escalated so they are not missed.'],
    ],
    manage: [
      ['Monthly Lead Recovery Review & Improvements', 'Review unworked inquiries, response times, and bookings tied to follow-up where trackable, plus one agreed improvement within scope.'],
      ['Ongoing System Monitoring & Support'],
    ],
    bestFor: 'Established service businesses seeking more consistent inquiry management and follow-up.',
  },
  {
    id: 'booking-growth', n: '02', name: 'Booking Growth System', featured: true,
    positioning: 'Booking, Appointment & Estimate Conversion Management',
    implement: [
      ['Everything In Lead Recovery'],
      ['Inquiry-To-Estimate Conversion Audit', 'Identify where prospects stall between inquiry, booking, appointment attendance, and estimate approval.'],
      ['Advanced Appointment Scheduling & Reminders'], ['Estimate & Proposal Follow-Up'], ['Cancellation & No-Show Recovery'], ['Past-Lead Reactivation & Re-Engagement'],
    ],
    manage: [
      ['Monthly Conversion Review & Workflow Testing', 'Each month, review one measurable bottleneck and test an agreed change within scope.'],
    ],
    bestFor: 'Growing businesses managing significant inquiry volume, customer appointments, and active sales pipelines.',
  },
  {
    id: 'multi-location', n: '03', name: 'Multi-Location Operations System',
    positioning: 'Cross-Location Routing, Team Standards & Operational Reporting',
    implement: [
      ['Everything In Booking Growth, Adapted For Multiple Locations'],
      ['Location-Based Routing & Overflow Coverage', 'Inquiries reroute to a designated backup team when the primary location is unavailable.'],
      ['Centralized CRM Management'], ['Standardized Workflows Across Teams'], ['Internal Notifications & Escalations'], ['Location-Specific Performance Dashboards'], ['Custom Integrations Subject To Scope'],
      ['Phased Rollout & Manager Training', 'Validate the workflows at the first location before rolling them out to the other locations in scope.'],
    ],
    manage: [
      ['Monthly Leadership Review & Routing Improvements', 'Review response times, unresolved inquiries, and routing exceptions by location.'],
    ],
    bestFor: 'Established businesses managing multiple locations, departments, or service territories.',
  },
].map((p) => ({ ...p, includes: [...p.implement, ...p.manage].map(([t]) => t) }));

const Y = true, N = false, S = 'scope';
export const COMPARE = [
  ['Missed-Call Recovery', Y, Y, Y],
  ['Automated Lead Follow-Up', Y, Y, Y],
  ['CRM Integration', Y, Y, Y],
  ['Appointment Scheduling', Y, Y, Y],
  ['Estimate & Proposal Follow-Up', N, Y, Y],
  ['Cancellation & No-Show Recovery', N, Y, Y],
  ['Past-Lead Reactivation', N, Y, Y],
  ['Customer Re-Engagement', N, Y, Y],
  ['Reporting & Analytics', Y, Y, Y],
  ['Multi-Location Routing', N, N, Y],
  ['Custom Integrations', N, S, S],
  ['Ongoing Support & Optimization', Y, Y, Y],
];

export const PROCESS = [
  { id: 'discover', title: 'Discover', body: 'We assess your business operations, customer journey, existing systems, and opportunities for improvement.' },
  { id: 'architect', title: 'Architect', body: 'We design a tailored communication and automation infrastructure aligned with your business requirements.' },
  { id: 'implement', title: 'Implement', body: 'We build, integrate, test, and launch your approved systems.' },
  { id: 'optimize', title: 'Optimize', body: 'We monitor performance, maintain workflows, and continuously refine your operations within the agreed service scope.' },
];

export const FAQ_INTRO = 'Whether you run an HVAC, roofing, plumbing, construction, or landscaping company, a dental practice, a med spa, or another service business, here is how YOVAI works and what to expect.';

export const FAQ = [
  {
    q: 'Who Is YOVAI For?',
    a: 'YOVAI builds communication and follow-up systems for service businesses. We work with HVAC, roofing, plumbing, construction, landscaping, dental, med spa, and other service businesses nationwide. Fit is confirmed during a consultation.',
  },
  {
    q: 'What Does Setup Involve?',
    a: 'Setup starts with discovery: how inquiries reach you today, who follows up, and which tools you use. We then configure your follow-up messages, pipeline, and notifications, connect the approved tools, test every workflow, and walk your staff through it. Nothing goes live until you approve it.',
  },
  {
    q: 'Will It Work With Our Existing Tools And Calendar?',
    a: 'Workflows run through a GoHighLevel pipeline. What we can connect, including your phone line, calendar, or booking system, and whether customers request a time or book directly, depends on the tools you already use and the configuration you approve. We review this during discovery and tell you before setup if something cannot be connected.',
  },
  {
    q: 'What Happens When A Customer Needs A Person?',
    a: 'Your team stays in charge. When a lead replies or needs attention, the owner or assigned staff member is notified so a person can take over the conversation and confirm next steps.',
  },
  {
    q: 'How Do You Measure Results?',
    a: 'We track follow-up attempts, replies, appointment requests, confirmed bookings, and verified completed jobs. Completed jobs count only after your team confirms them. Numbers from the opportunity estimator are estimates based on your own inputs and are never reported as results.',
  },
  {
    q: 'How Is Pricing Determined?',
    a: 'Service fees, implementation costs, billing schedules, and ongoing support arrangements are defined in each client\u2019s approved proposal and service agreement. We recommend a package after your consultation, based on your operations, locations, and the scope you approve.',
  },
  {
    q: 'How Do Payments Work?',
    a: 'Your setup fee is split into two payments: 50% to begin and 50% after agreed testing passes, before launch. Your first monthly subscription payment is also due at launch, with recurring billing beginning on that date.',
  },
  {
    q: 'What Does Testing Confirm?',
    a: 'Before launch, we run your system against the testing checklist agreed in your scope. Passing that checklist confirms the agreed functionality works as scoped. It does not guarantee bookings, revenue, or other business results.',
  },
];
